import { action, computed, makeObservable, observable } from 'mobx';
import { VariantStore } from './VariantStore';
import { ArticleState, ILocalization } from '../types';
import { IResponseData, IResponseDataRefresh } from './types';
import DataStore from './DataStore';
import OrderModuleStore from './OrderModuleStore';
import { ScrollLock } from '../helpers/scrollLock';
import { LinkScroll } from '../helpers/linkScroll';
import { EsAppHelper } from '../helpers/esAppHelper';
import { MixedMediaStore } from './MixedMediaStore';
import { Fetch } from './Fetch';
import { ProductInfoStore } from './ProductInfoStore';
import { TrackingHelper, ArticleTrackingData } from '@escid-espp1/helpers';
import { getParamCaseInsensitive } from '../helpers/urlHelper';

export class MainStore {
    public readonly dataStore: DataStore;
    public readonly orderModuleStore: OrderModuleStore;
    public readonly variantStore: VariantStore;
    public readonly mixedMediaStore: MixedMediaStore;
    public readonly productInfoStore: ProductInfoStore;
    public readonly scrollLock: ScrollLock;
    public readonly linkScroll: LinkScroll;
    public readonly esAppHelper = new EsAppHelper();
    public fetch: Fetch;

    private orderModuleRef: HTMLDivElement | undefined;

    // @observable:
    public fetchedData: IResponseData;
    public dataRefresh: IResponseDataRefresh | undefined;
    public cdnAssetsUrl: string;
    public isGlobal: boolean;
    public isMobileShop: boolean;
    public localization: ILocalization;
    public wwsId;
    public showKioskLayout: boolean;
    public enableKraken: boolean;
    public disableDirectEcommerceTracking: boolean;
    public debuggerIsOn: boolean;
    public isEsApp: boolean;
    public stickyHeaderTop: number;
    public trackingHelper: TrackingHelper;
    public basketItemId: string | null;

    constructor(initialData: IResponseData) {
        this.dataStore = new DataStore(initialData, this);
        this.scrollLock = new ScrollLock();
        this.linkScroll = LinkScroll.instance;
        this.fetchedData = initialData;
        this.orderModuleStore = new OrderModuleStore(this, initialData.contextData.priceMode);
        this.variantStore = new VariantStore(this);
        this.mixedMediaStore = new MixedMediaStore(this);
        this.productInfoStore = new ProductInfoStore(this);

        this.fetch = new Fetch(this.fetchedData.contextData.apiLink);

        this.localization = initialData.localizationObject;
        this.isMobileShop = initialData.contextData.isMobile;
        this.isGlobal = initialData.contextData.portal.includes('.GLOBAL.');
        this.wwsId = initialData.contextData.wwsId;
        this.showKioskLayout = false; //his.wwsId != null;
        this.enableKraken = initialData.contextData.featureToggles.enableKraken;
        this.disableDirectEcommerceTracking = initialData.contextData.featureToggles.disableDirectEcommerceTracking;

        this.cdnAssetsUrl = initialData.contextData.baseUrlForAssets;
        this.debuggerIsOn = false;
        this.isEsApp = initialData.contextData.isEsApp;
        this.stickyHeaderTop = 0;
        const featureToggles = initialData.contextData.featureToggles;
        const urlParams = typeof window !== 'undefined' ? new URL(window.location.href).searchParams : null;
        this.basketItemId = urlParams ? getParamCaseInsensitive(urlParams, 'BasketItemId') : null;

        const caseInsensitiveParameters = new URLSearchParams();

        if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
            const originParameters = new URL(window.location.href).searchParams;
            originParameters.forEach((value: string, key: string) => {
                caseInsensitiveParameters.append(key.toLowerCase(), value);
            });
        }

        const itemOrigin = caseInsensitiveParameters.get('itemorigin') ||
            'NAVIGATION/' + this.dataStore.category.name || '';

        this.trackingHelper = new TrackingHelper(featureToggles.piwikProEnableEcommerceTracking,
            featureToggles.enableMetaPixel, featureToggles.enableTikTokPixel, featureToggles.enableKraken,
            initialData.contextData.featureToggles.enableClientLogging, itemOrigin, this.basketItemId);

        makeObservable(this, {
            fetchedData: observable,
            cdnAssetsUrl: observable,
            wwsId: observable,
            showKioskLayout: observable,
            stickyHeaderTop: observable,
            debuggerIsOn: observable,
            variantChanged: action,
            addBrick: action,
            removeBrick: action,
            switchSetImage: action,
            setStickyHeaderTop: action,
            getHasObsoleteBricks: computed,

        });

        if (typeof document !== 'undefined') {
            document.addEventListener('DOMContentLoaded', () => this.trackVisitedArticle());

            window.shell.subscribeTo('ESPP.BasketModal.BasketItemIdChange', ({ basketItemId }) => {
                this.basketItemId = basketItemId;
            })
        }
    }

    public trackVisitedArticle() {
        const trackingData: ArticleTrackingData = {
            id: this.dataStore.orderModule.flexSetInfo.fsCode,
            name: this.dataStore.orderModule.flexSetInfo.displayName,
            price: this.dataStore.orderModule.flexSetPrice.price,
            currencyCode: this.dataStore.orderModule.flexSetPrice.currency.isoCode,
            brand: 'Strauss',
            category: this.dataStore.category.navigationKeyPath,
            variantId: this.basketItemId || '',
        };

        if (this.enableKraken) {
            this.trackingHelper.krakenTrackProductViewed(trackingData, 'ESPP3.ProductDetailsPage');
        }

        if (!this.disableDirectEcommerceTracking) {
            this.trackingHelper.piwikTrackVisitedArticle(trackingData, this.orderModuleStore.articleCount);
            this.trackingHelper.metaTrackVisitedArticle(trackingData);
            this.trackingHelper.tiktokTrackVisitedArticle(trackingData);
        }
    }

    public trackVisitedArticleOnVariantChange() {
        const trackingData: ArticleTrackingData = {
            id: this.dataStore.orderModule.flexSetInfo.fsCode,
            name: this.dataStore.orderModule.flexSetInfo.displayName,
            price: this.dataStore.orderModule.flexSetPrice.price,
            currencyCode: this.dataStore.orderModule.flexSetPrice.currency.isoCode,
            brand: '',
            category: this.dataStore.category.name,
            variantId: this.basketItemId || '',
        };

        if (this.enableKraken) {
            this.trackingHelper.krakenTrackProductViewed(trackingData, 'ESPP3.ProductDetailsPage');
        }

        if (!this.disableDirectEcommerceTracking) {
            this.trackingHelper.metaTrackVisitedArticle(trackingData);
            this.trackingHelper.tiktokTrackVisitedArticle(trackingData);
        }
    }

    public variantChanged(fetchedDataRefresh: IResponseDataRefresh) {
        //hardcode price disclaimer for GLB. In future will be removed. Just tmp solution
        // this.insertDisclaimerForGLB(fetchedData);

        // this.setFetchedData(fetchedData); ????
        // this.dataStore.updateData(fetchedData); ????
        // this.mixedMediaStore.dataUpdate(fetchedDataRefresh.mixedMediaForBrick); ????

        this.dataStore.dataRefresh(fetchedDataRefresh);
        this.trackVisitedArticleOnVariantChange();
    }

    get getHasObsoleteBricks() {
        let result = false;

        this.dataStore.orderModule.sortedBrickSelections.forEach((brickSelection) => {
            let obsoleteCount = 0;
            const brickItems = this.dataStore.sortedBrickItems.filter(it =>
                it.brickNo === brickSelection?.brickNo);
            brickItems.forEach((brickItem) => {
                if (brickItem.articleState === ArticleState.Obsoleted) {
                    obsoleteCount = obsoleteCount + 1;
                }
            });

            if (obsoleteCount === brickItems.length) {
                result = true;
                return;
            }
        });

        return result;
    }

    public getSalesArticleNo(salesArticleVariantKey: string | undefined): string {
        if (!salesArticleVariantKey) {
            return 'sold_out';
        }

        return salesArticleVariantKey.split('-')[0];
    }

    // public checkSelectedSizeType(variantSelection: IVariantSelection) {
    //     const isFirstSelectedTypeSoldOut =
    //         variantSelection.availableSizeTypes.find(sizeType => {
    //             if (sizeType.sizeTypeName == variantSelection.selectedSizeType)
    //                 return sizeType.sizes.every(size => {
    //                     return size.salesArticleVariantKey == '';
    //                 });
    //         }) != null;

    //     if (isFirstSelectedTypeSoldOut) {
    //         const nonSoldOutSizeType = variantSelection.availableSizeTypes.find(sizeType => {
    //             return !sizeType.sizes.every(size => {
    //                 return size.salesArticleVariantKey == '';
    //             });
    //         });
    //         if (nonSoldOutSizeType) {
    //             variantSelection.selectedSizeType = nonSoldOutSizeType.sizeTypeName;
    //             variantSelection.selectedSize = nonSoldOutSizeType.sizes.find(size => {
    //                 return size.salesArticleVariantKey;
    //             })?.size;
    //         }
    //     }
    // }

    addBrick() {
        this.dataStore.mixedMedia.sortedMixedMediaForBricks.push(this.dataStore.mixedMedia.sortedMixedMediaForBricks[0]);
    }

    removeBrick() {
        this.dataStore.mixedMedia.sortedMixedMediaForBricks.pop();
    }

    switchSetImage() {
        if (this.dataStore.mixedMedia.flexibleSetImageFullPath == ''
            || this.dataStore.mixedMedia.flexibleSetImageFullPath == null) {
            this.dataStore.mixedMedia.flexibleSetImageFullPath = 'https://cdn-preview-dev.strauss.com/de/assets/fsp/images_preview/Original/product/1.Preview.9086680/Universalduebel_UD_im_192-teiligen_Set-12915-1-636637342490893696.jpg';
        }
        else {
            this.dataStore.mixedMedia.flexibleSetImageFullPath = '';
        }
    }

    setStickyHeaderTop(): void {
        const header = typeof document !== 'undefined' && document.querySelector('.mkt-headr');
        this.stickyHeaderTop = header ? header.getBoundingClientRect().height : 50;
    }

    public hideEsAppButton() {
        this.esAppHelper.navigationWheel.hide();
    }

    public showEsAppButton() {
        this.esAppHelper.navigationWheel.show();
    }

    turnOnDebugger() {
        this.debuggerIsOn = !this.debuggerIsOn;
    }

    public setLinkScroll(mixMediaRef: HTMLDivElement, floatRef: HTMLDivElement) {
        this.linkScroll.setComponent(this.scrollLock, this.orderModuleRef!, floatRef, mixMediaRef);
    }

    public setOrderModuleRef(orderModuleRef: HTMLDivElement) {
        this.orderModuleRef = orderModuleRef
    }
}
